import { find, get, uniq } from "lodash";
import { useMemo, useState } from "react";
import Button from "shared/components/Button";
import Header from "shared/components/Header";
import Loading from "shared/components/Spin";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { GENERATE_SUBSCRIPTION_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { UPDATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { CURRENCY_SYMBOL } from "utils/constants";

const formatCost = (cost) => Number(cost || 0).toFixed(2);

const SelectCombination = ({ project, onNext, onBack }) => {
  const [submitting, setSubmitting] = useState(false);
  const [selectedCombinationId, setSelectedCombinationId] = useState(
    get(project, "fields.selectedCombinationId"),
  );

  const combinations = useMemo(
    () => get(project, "fields.combinations", []),
    [project],
  );
  const upsellsByBase = useMemo(
    () => get(project, "fields.upsells", {}),
    [project],
  );
  const baseInsurancePlanIds = uniq(
    combinations.map(({ baseInsurancePlanId }) => baseInsurancePlanId),
  );

  const { data, loading } = useQuery(GENERAL_COSTS, {
    skip: baseInsurancePlanIds.length === 0,
    variables: {
      where: {
        project: { id: project.id },
      },
      isIn: {
        insurancePlan: { id: baseInsurancePlanIds },
      },
    },
  });

  const baseGeneralCosts = get(data, "generalCosts.data", []);

  const enrichedCombinations = useMemo(() => {
    return combinations.map((combination) => {
      const baseGeneralCost = find(
        baseGeneralCosts,
        (generalCost) =>
          get(generalCost, "insurancePlan.id") ===
          combination.baseInsurancePlanId,
      );
      const baseInsurancePlan = get(baseGeneralCost, "insurancePlan");
      const baseCost = get(baseGeneralCost, "cost", 0);
      const currency = get(baseGeneralCost, "currency");
      const complementModule = combination.complementaryInsurancePlanId
        ? find(
            get(upsellsByBase, combination.baseInsurancePlanId, []),
            (module) =>
              get(module, "insurancePlan.id") ===
              combination.complementaryInsurancePlanId,
          )
        : null;
      const complementaryInsurancePlan = get(complementModule, "insurancePlan");
      const complementCost = get(complementModule, "cost", 0);
      const baseName = get(baseInsurancePlan, "name", "");
      const complementName = get(complementaryInsurancePlan, "name");
      return {
        ...combination,
        baseInsurancePlan,
        complementaryInsurancePlan,
        baseCost,
        complementCost,
        totalCost: baseCost + complementCost,
        currency,
        label: complementName ? `${baseName} avec ${complementName}` : baseName,
      };
    });
  }, [combinations, baseGeneralCosts, upsellsByBase]);

  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION);
  const [generateSubscriptionDocuments] = useMutation(
    GENERATE_SUBSCRIPTION_DOCUMENTS,
  );

  const onSubmit = () => {
    const combination = find(
      enrichedCombinations,
      ({ id }) => id === selectedCombinationId,
    );
    if (!combination) return;
    const subscriptionId = get(project, "fields.currentSubscription");
    if (!subscriptionId) return;
    setSubmitting(true);
    updateSubscription({
      variables: {
        where: { id: subscriptionId },
        data: {
          insurancePlan: { id: combination.baseInsurancePlanId },
          additionalInfo: {
            combinationId: combination.id,
            complementaryInsurancePlanId:
              combination.complementaryInsurancePlanId || null,
          },
        },
      },
      onCompleted: () =>
        generateSubscriptionDocuments({
          variables: {
            data: { id: subscriptionId },
          },
          onCompleted: () =>
            onNext({
              payload: {
                fields: {
                  ...get(project, "fields", {}),
                  currentSubscription: subscriptionId,
                  selectedCombinationId: combination.id,
                },
              },
              onCompleted: () => setSubmitting(false),
            }),
          onError: () => setSubmitting(false),
        }),
      onError: () => setSubmitting(false),
    });
  };

  if (loading) return <Loading />;

  return (
    <div className="manual-select-combination">
      <Header
        title="Sélectionnez la formule à signer"
        subtitle="Choisissez la combinaison qui sera utilisée pour la signature électronique."
      />
      <div className="manual-select-combination__list">
        {enrichedCombinations.map((combination) => {
          const symbol = CURRENCY_SYMBOL[combination.currency] || "";
          const isSelected = selectedCombinationId === combination.id;
          return (
            <div
              key={combination.id}
              className={`manual-select-combination__card${
                isSelected ? " manual-select-combination__card--selected" : ""
              }`}
              onClick={() => setSelectedCombinationId(combination.id)}
            >
              <div className="manual-select-combination__card--header">
                <div className="manual-select-combination__card--name">
                  {combination.label}
                </div>
                <div className="manual-select-combination__card--price">
                  {formatCost(combination.totalCost)} {symbol} / mois
                </div>
              </div>
              <div className="manual-select-combination__card--detail">
                <div>
                  <span>{get(combination, "baseInsurancePlan.name", "—")}</span>
                  <span>
                    {formatCost(combination.baseCost)} {symbol}
                  </span>
                </div>
                {combination.complementaryInsurancePlan && (
                  <div>
                    <span>
                      {get(combination, "complementaryInsurancePlan.name", "—")}
                    </span>
                    <span>
                      {formatCost(combination.complementCost)} {symbol}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="manual-select-combination__actions">
        <Button type="default" onClick={onBack}>
          Retour
        </Button>
        <Button
          type="primary"
          onClick={onSubmit}
          disabled={!selectedCombinationId || submitting}
          loading={submitting}
        >
          Confirmer la formule
        </Button>
      </div>
    </div>
  );
};

export default SelectCombination;
